import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Bell, CheckCircle2, XCircle, Coins, Package, RotateCcw, Inbox } from "lucide-react";

const ICONS = {
  hours_approved: { Icon: CheckCircle2, color: "var(--cm-secondary)" },
  hours_rejected: { Icon: XCircle, color: "#A8533F" },
  listing_redeemed: { Icon: Coins, color: "var(--cm-primary)" },
  listing_delivered: { Icon: Package, color: "#2C5F7C" },
  listing_received: { Icon: CheckCircle2, color: "var(--cm-secondary)" },
  listing_cancelled: { Icon: RotateCcw, color: "#5C6B61" },
};

function timeAgo(iso) {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  const diff = Date.now() - t;
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return "just now";
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const d = Math.floor(hr / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

export default function NotificationBell() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const nav = useNavigate();

  const fetch = async () => {
    try {
      const { data } = await api.get("/me/notifications");
      setItems(data.items);
      setUnread(data.unread_count);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => {
    fetch();
    const id = setInterval(fetch, 30_000);
    return () => clearInterval(id);
  }, []);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const handleOpen = async () => {
    setOpen((o) => !o);
    if (!open && unread > 0) {
      await api.post("/me/notifications/mark-read", {});
      setUnread(0);
      // Optimistically mark items read locally
      setItems((arr) => arr.map((n) => ({ ...n, read: true })));
    }
  };

  const clickItem = (n) => {
    setOpen(false);
    if (n.link) nav(n.link);
  };

  return (
    <div className="relative" ref={ref}>
      <button
        data-testid="notification-bell-btn"
        onClick={handleOpen}
        className="relative h-9 w-9 rounded-full grid place-items-center border border-ink-soft hover:bg-white/60 transition-colors"
        aria-label="Notifications"
      >
        <Bell className="h-4 w-4" strokeWidth={2} />
        {unread > 0 && (
          <span
            data-testid="notification-unread-badge"
            className="absolute -top-1 -right-1 h-5 min-w-[20px] px-1 rounded-full bg-[var(--cm-primary)] text-white text-[10px] font-bold grid place-items-center border-2 border-[var(--cm-bg)]"
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="notification-dropdown"
          className="absolute right-0 mt-2 w-80 md:w-96 card-soft shadow-lg z-50 max-h-[480px] overflow-hidden flex flex-col"
        >
          <div className="px-4 py-3 border-b border-ink-soft flex items-center justify-between">
            <span className="font-display font-semibold">Notifications</span>
            <span className="overline text-ink-soft">{items.length} total</span>
          </div>
          <div className="overflow-y-auto flex-1">
            {items.length === 0 ? (
              <div className="p-8 text-center text-ink-soft" data-testid="notification-empty">
                <Inbox className="h-6 w-6 mx-auto opacity-60" />
                <p className="text-sm mt-2">You're all caught up.</p>
              </div>
            ) : (
              items.map((n) => {
                const cfg = ICONS[n.kind] || { Icon: Bell, color: "var(--cm-ink-soft)" };
                return (
                  <button
                    key={n.id}
                    data-testid={`notification-item-${n.id}`}
                    onClick={() => clickItem(n)}
                    className={`w-full text-left px-4 py-3 flex items-start gap-3 border-b border-ink-soft last:border-b-0 hover:bg-white/50 transition-colors ${
                      !n.read ? "bg-amber-50/40" : ""
                    }`}
                  >
                    <span
                      className="h-8 w-8 rounded-full grid place-items-center text-white flex-shrink-0"
                      style={{ background: cfg.color }}
                    >
                      <cfg.Icon className="h-3.5 w-3.5" />
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm leading-snug">{n.message}</p>
                      <p className="text-xs text-ink-soft mt-1">{timeAgo(n.created_at)}</p>
                    </div>
                    {!n.read && <span className="h-2 w-2 rounded-full bg-[var(--cm-primary)] mt-1.5 flex-shrink-0" />}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
